import { useEffect, useState } from 'react';
import {
  GoAButton,
  GoACallout,
  GoADivider,
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { EligibilityQuestion } from '../../models/eligibility';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { KeyValuePair } from '../../models/key-value-pair';
import {
  clearCollision,
  updateCollision,
} from '../../state/slices/collisionSlice';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import { useNavigate } from 'react-router-dom';
import { isUserLoggedIn, logout } from '../../state/slices/authSlice';

const errorHeader = 'You do not meet the criteria to use Report My Collision';

export const hitAndRunFlowQuestions: EligibilityQuestion[] = [
  new EligibilityQuestion(
    1,
    'Do you have any information on the driver or the vehicle that committed the hit and run?',
    true,
    errorHeader,
    `
    You cannot use the Report My Collision online service to report a hit and run if witness information
    or camera footage is available to identify the other driver or their vehicle. Visit your local police station
    or reporting center to file a report.
    Refer to the links below for more details.<br/><br/>
    <a href='https://www.calgary.ca/cps/calgary-police-service-district-offices.html'> Calgary Police Service </a> <br/>
    <a href='https://www.edmontonpolice.ca/ContactEPS/EPSBranchLocations'> Edmonton Polic Service </a> <br/>
    <a href='https://www.rcmp-grc.gc.ca/detach/en/find/AB'> RCMP </a>
  `,
    'important',
    'for example witness or footage from a street/dash camera provided identifying information',
  ),

  new EligibilityQuestion(
    2,
    "Do you have an Alberta driver's licence?",
    false,
    errorHeader,
    "Collisions involving drivers without an Alberta driver's licence are not supported by the online service.",
    'important',
    "Only drivers with an Alberta driver's licence may use the online service",
  ),
  new EligibilityQuestion(
    3,
    'Did your collision occur in Alberta?',
    false,
    errorHeader,
    'Only collisions that occurred in Alberta are supported by the online service at this time.',
    'important',
  ),
  new EligibilityQuestion(
    4,
    'Have police started a collision report for you already?',
    true,
    errorHeader,
    `
    If a police report for your collision has already been started or completed, please contact the police agency in the jurisdiction where the collision has occurred for additional information.
    Only new single vehicle collisions without an existing police report are eligible to report using the online service.
    `,
    'important',
  ),
  new EligibilityQuestion(
    5,
    'Are you reporting as a driver of a passenger vehicle?',
    false,
    errorHeader,
    `
    Only collisions that involved a passenger vehicle are supported by the online service at this time.
    `,
    'important',
    `
    Collisions involving commercial carriers are not supported at this time. <br/>
    For more information about regarding commercial carriers, please visit: <a href='https://www.alberta.ca/commercial-carriers'>Commercial carriers</a>
    `,
  ),
  new EligibilityQuestion(
    6,
    'Will you be providing photos of your vehicle damage?',
    false,
    errorHeader,
    `
    You must include photos or video of the vehicle damage to get your damage sticker.
    Submitting the report without these images will delay or prevent you from obtaining the damage sticker.
    `,
    'important',
    'You will need to upload photos of the damaged area(s) on your vehicle before submitting your final report',
  ),
];

export const regularFlowQuestions: EligibilityQuestion[] = [
  new EligibilityQuestion(
    1,
    'Is there an injury related to your collision?',
    true,
    errorHeader,
    'If you require immediate medical assistance, please dial 911.',
    'emergency',
    'Individuals that have sustained injuries due to a collision (e.g, have visited the hospital/emergency room or received treatment), are not eligible to use this online service.',
  ),
  new EligibilityQuestion(
    2,
    "Do you have an Alberta driver's licence?",
    false,
    errorHeader,
    'Collisions involving drivers without a valid Alberta licence are not supported by the online service.',
    'important',
    "Only drivers with an Alberta driver's licence may use the online service",
  ),
  new EligibilityQuestion(
    3,
    'Did your collision occur in Alberta?',
    false,
    errorHeader,
    'Only collisions that occurred in Alberta are supported by the online service at this time.',
    'important',
  ),
  new EligibilityQuestion(
    4,
    'Have police started a collision report for you already?',
    true,
    errorHeader,
    `
    If a police report for your collision has already been started or completed, please contact the police agency in the jurisdiction where the collision has occurred for additional information.
    Only new single vehicle collisions without an existing police report are eligible to report using the online service.
    `,
    'important',
  ),
  new EligibilityQuestion(
    5,
    'Are you reporting as a driver of a passenger vehicle?',
    false,
    errorHeader,
    `
    Only collisions that involved a passenger vehicle are supported by the online service at this time.
    `,
    'important',
    "Collisions involving commercial carriers are not supported at this time. For more information regarding commercial carriers, please visit: <a href='https://www.alberta.ca/commercial-carriers'>Commercial carriers</a>",
  ),
  new EligibilityQuestion(
    6,
    'Will you be providing photos of your vehicle damage?',
    false,
    errorHeader,
    `
    You must include photos or video of the vehicle damage to get your damage sticker.
    Submitting the report without these images will delay or prevent you from obtaining the damage sticker.
    `,
    'important',
    'You will need to upload photos of the damaged area(s) on your vehicle before submitting your final report',
  ),
  new EligibilityQuestion(
    7,
    'Did your collision involve another vehicle and driver?',
    true,
    errorHeader,
    `
    Only single vehicle collisions involving one driver are supported by the online service at this time.
    `,
    'important',
  ),
  new EligibilityQuestion(
    8,
    'Did your collision occur on private property?',
    true,
    errorHeader,
    `Examples of private property include residential driveways or garages, parking lots at malls, apartment buildings, or businesses, underground parking areas, gated residential communities with restricted access, private roads not maintained by the municipality, and privately owned land such as farms or acreages.`,
    'important',
  ),
];
export interface EligibilityProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const EligibilityComponent = ({
  navigationConfiguration,
}: EligibilityProps) => {
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateAuth = useAppSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const errorText = 'Choose yes or no to complete the eligibility questions';

  const [wasHitAndRun, setWasHitAndRun] = useState<boolean | undefined>(
    stateCollision.collision.wasHitAndRun,
  );

  const [hitAndRunAnswers, setHitAndRunAnswers] = useState<
    KeyValuePair<number, boolean | undefined>[]
  >(stateCollision.collision.eligibility);

  const [regularFlowAnswers, setRegularFlowAnswers] = useState<
    KeyValuePair<number, boolean | undefined>[]
  >(stateCollision.collision.eligibility);

  const [formError, setFormError] = useState('');
  const [nextPage, setNextPage] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    isUserLoggedIn(stateAuth) ? true : false,
  );

  // Load nextPage from config
  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    if (
      isLoggedIn &&
      stateCollision.collision &&
      stateCollision.collision?.agreementToTermsAndConditions
    ) {
      setNextPage(pageConfig?.default ?? 'error');
    } else {
      setNextPage('#register');
    }
  }, [navigationConfiguration]);

  useEffect(() => {
    setWasHitAndRun(stateCollision.collision.wasHitAndRun);

    if (stateCollision.collision.eligibility.length > 0) {
      // Load saved answers into the correct state based on wasHitAndRun
      if (stateCollision.collision.wasHitAndRun) {
        setHitAndRunAnswers([...stateCollision.collision.eligibility]);
      } else {
        setRegularFlowAnswers([...stateCollision.collision.eligibility]);
      }
    }
  }, [
    stateCollision.collision.eligibility,
    stateCollision.collision.wasHitAndRun,
  ]);

  //--- Helper functions shared by both flows ---
  const checkIfInError = (
    answers: KeyValuePair<number, boolean | undefined>[],
    id: number,
  ): boolean => {
    const answer = answers.find((x) => x.id === id);
    return !!(formError.length > 0 && answer?.value === undefined);
  };

  const hasWarningTriggered = () => {
    // We only care about whichever flow is relevant
    if (wasHitAndRun === null || wasHitAndRun === undefined) {
      return false;
    }
    if (wasHitAndRun) {
      // If we’re in the hit-and-run flow, check hitAndRunAnswers
      return hitAndRunAnswers.some((a) => a.value === false);
    } else {
      // Otherwise check regularFlowAnswers
      return regularFlowAnswers.some((a) => a.value === false);
    }
  };

  const hasAllQuestionsAnswered = () => {
    // We only care about whichever flow is relevant
    if (wasHitAndRun === null || wasHitAndRun === undefined) {
      return false;
    }
    if (wasHitAndRun) {
      // If we’re in the hit-and-run flow, check hitAndRunAnswers
      return hitAndRunFlowQuestions.length === hitAndRunAnswers.length;
    } else {
      // Otherwise check regularFlowAnswers
      return regularFlowQuestions.length === regularFlowAnswers.length;
    }
  };

  const getFormError = (
    answers: KeyValuePair<number, boolean | undefined>[],
    id: number,
  ): string => {
    return checkIfInError(answers, id) ? errorText : '';
  };

  const getItemValue = (
    questions: EligibilityQuestion[],
    answers: KeyValuePair<number, boolean | undefined>[],
    id: number,
  ): string => {
    const question = questions.find((x) => x.id === id);
    const answer = answers.find((x) => x.id === id);
    if (answer?.value === undefined) return '';

    if (
      (answer.value && !question?.ineligiblityTrigger) ||
      (!answer.value && question?.ineligiblityTrigger)
    ) {
      return 'Yes';
    }
    if (
      (answer.value && question?.ineligiblityTrigger) ||
      (!answer.value && !question?.ineligiblityTrigger)
    ) {
      return 'No';
    }
    return '';
  };

  const updateAnswers = (
    setAnswerFn: React.Dispatch<
      React.SetStateAction<KeyValuePair<number, boolean | undefined>[]>
    >,
    id: number,
    newValue: boolean | undefined,
  ) => {
    setAnswerFn((prevAnswers) => {
      const existingAnswer = prevAnswers.find((answer) => answer.id === id);
      if (existingAnswer) {
        return prevAnswers.map((answer) =>
          answer.id === id ? { ...answer, value: newValue } : answer,
        );
      } else {
        const newAnswer: KeyValuePair<number, boolean | undefined> = {
          id,
          value: newValue,
        };
        return [...prevAnswers, newAnswer];
      }
    });
  };

  const handleFlowSave = () => {
    // Merge whichever answers you need into your collision object
    const updatedCollision = {
      ...stateCollision.collision,
      isEligible: true,
      wasHitAndRun: wasHitAndRun, // So you remember which flow they took
      eligibility: wasHitAndRun //store the appropriate eligibility collection responses accordingly
        ? [...hitAndRunAnswers]
        : [...regularFlowAnswers],
    };
    dispatch(updateCollision(updatedCollision));
    navigationConfiguration.save?.();
  };

  const validateFlowAnswers = (): boolean => {
    dispatch(removeAllValidationErrorNotifications());

    // pick which answers array to check
    const questionsToAsk = wasHitAndRun
      ? hitAndRunFlowQuestions
      : regularFlowQuestions;
    const answersToCheck = wasHitAndRun ? hitAndRunAnswers : regularFlowAnswers;

    // ensure all answered
    if (
      answersToCheck.length != questionsToAsk.length ||
      answersToCheck.some((x) => x.value === undefined)
    ) {
      setFormError(errorText);
      dispatch(
        pushNotification({
          body: errorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      return false;
    }
    return true;
  };

  // 1) Create a helper function
  function shouldShowQuestion(
    questions: EligibilityQuestion[],
    answers: KeyValuePair<number, boolean | undefined>[],
    currentIndex: number,
  ): boolean {
    // Always show the first question
    if (currentIndex === 0) return true;

    // Find the previous question and its answer
    const prevQuestion = questions[currentIndex - 1];
    const prevAnswer = answers.find((x) => x.id === prevQuestion.id);

    // If the previous question was never answered, hide this one
    if (!prevAnswer || prevAnswer.value === undefined) {
      return false;
    }

    console.log('Previous Answer triggered:', prevAnswer.value);

    return prevAnswer.value;
  }

  return (
    <div>
      <GoASpacer vSpacing="l" />
      <GoAFormItem
        error={
          formError && (wasHitAndRun === null || wasHitAndRun == undefined)
            ? errorText
            : ''
        }
      >
        <div style={{ font: 'var(--goa-typography-body-l' }}>
          Was your collision a parked vehicle hit and run?
        </div>
        <GoASpacer vSpacing="m" />
        <GoARadioGroup
          name="wasHitAndRun"
          testId="initial-prompt"
          value={
            wasHitAndRun === null || wasHitAndRun == undefined
              ? ''
              : wasHitAndRun
                ? 'Yes'
                : 'No'
          }
          error={
            !!(
              formError &&
              (wasHitAndRun === null || wasHitAndRun == undefined)
            )
          }
          onChange={(name, value) => {
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());
            setWasHitAndRun(value === 'Yes');
          }}
        >
          <GoARadioItem value="Yes">Yes</GoARadioItem>
          <GoARadioItem value="No">No</GoARadioItem>
        </GoARadioGroup>
      </GoAFormItem>
      <GoASpacer vSpacing="l" />
      <GoADivider />
      {wasHitAndRun === true && (
        <>
          <GoASpacer vSpacing="l" />
          {hitAndRunFlowQuestions.map((question, index) => {
            // If the helper says "false," skip rendering this question
            if (
              !shouldShowQuestion(
                hitAndRunFlowQuestions,
                hitAndRunAnswers,
                index,
              )
            ) {
              return null;
            }

            return (
              <div key={question.id}>
                <GoASpacer vSpacing="l" />
                <GoAFormItem
                  error={getFormError(hitAndRunAnswers, question.id)}
                >
                  <div style={{ font: 'var(--goa-typography-body-l' }}>
                    {question.description}
                  </div>
                  <GoASpacer vSpacing="m" />
                  <GoARadioGroup
                    name={`question-${question.id}`}
                    testId={`question-${question.id}`}
                    value={getItemValue(
                      hitAndRunFlowQuestions,
                      hitAndRunAnswers,
                      question.id,
                    )}
                    error={checkIfInError(hitAndRunAnswers, question.id)}
                    onChange={(item, value) => {
                      setFormError('');
                      dispatch(removeAllValidationErrorNotifications());
                      if (
                        (question.ineligiblityTrigger && value === 'Yes') ||
                        (!question.ineligiblityTrigger && value === 'No')
                      ) {
                        updateAnswers(setHitAndRunAnswers, question.id, false);
                        return;
                      }
                      updateAnswers(setHitAndRunAnswers, question.id, true);
                    }}
                  >
                    <GoARadioItem value="Yes">Yes</GoARadioItem>
                    <GoARadioItem value="No">No</GoARadioItem>
                    <div
                      style={{
                        display: (() => {
                          const ans = hitAndRunAnswers.find(
                            (x) => x.id === question.id,
                          );
                          if (!ans || ans.value === true) {
                            return 'none';
                          }
                          return 'block';
                        })(),
                      }}
                    >
                      <div style={{ display: 'flex', marginTop: '16px' }}>
                        <div
                          style={{
                            backgroundColor:
                              'var(--Color-Greyscale-200, #DCDCDC)',
                            width: '6px',
                            marginLeft: '10px',
                          }}
                        ></div>
                        <GoACallout
                          type={question.calloutType}
                          heading={question.errorHeader}
                          mb="l"
                          ml="xl"
                          mr="2xl"
                          mt="l"
                          testId={`callout-${question.id}`}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: question.errorBody,
                            }}
                          />
                        </GoACallout>
                      </div>
                    </div>

                    {question.helperText && (
                      <div
                        style={{ font: 'var(--goa-typography-body-xs)' }}
                        dangerouslySetInnerHTML={{
                          __html: question.helperText,
                        }}
                      />
                    )}
                  </GoARadioGroup>
                </GoAFormItem>
                <GoASpacer vSpacing="l" />
                <GoADivider />
              </div>
            );
          })}
        </>
      )}

      {wasHitAndRun === false && (
        <>
          <GoASpacer vSpacing="l" />

          {regularFlowQuestions.map((question, index) => {
            // If the helper says "false," skip rendering this question
            if (
              !shouldShowQuestion(
                regularFlowQuestions,
                regularFlowAnswers,
                index,
              )
            ) {
              return null;
            }

            return (
              <div key={question.id}>
                <GoASpacer vSpacing="l" />
                <GoAFormItem
                  error={getFormError(regularFlowAnswers, question.id)}
                >
                  <div style={{ font: 'var(--goa-typography-body-l' }}>
                    {question.description}
                  </div>
                  <GoASpacer vSpacing="m" />
                  <GoARadioGroup
                    name={`question-${question.id}`}
                    testId={`question-${question.id}`}
                    value={getItemValue(
                      regularFlowQuestions,
                      regularFlowAnswers,
                      question.id,
                    )}
                    error={checkIfInError(regularFlowAnswers, question.id)}
                    onChange={(item, value) => {
                      setFormError('');
                      dispatch(removeAllValidationErrorNotifications());
                      if (
                        (question.ineligiblityTrigger && value === 'Yes') ||
                        (!question.ineligiblityTrigger && value === 'No')
                      ) {
                        updateAnswers(
                          setRegularFlowAnswers,
                          question.id,
                          false,
                        );
                        return;
                      }
                      updateAnswers(setRegularFlowAnswers, question.id, true);
                    }}
                  >
                    <GoARadioItem value="Yes" label="Yes" />
                    <GoARadioItem value="No" label="No" />

                    {/* Callout for ineligibility, only shown if user picks the ineligible path */}
                    <div
                      style={{
                        display: (() => {
                          const ans = regularFlowAnswers.find(
                            (x) => x.id === question.id,
                          );
                          if (!ans || ans.value === true) {
                            return 'none';
                          }
                          return 'block';
                        })(),
                      }}
                    >
                      <div style={{ display: 'flex', marginTop: '16px' }}>
                        <div
                          style={{
                            backgroundColor:
                              'var(--Color-Greyscale-200, #DCDCDC)',
                            width: '6px',
                            marginLeft: '10px',
                          }}
                        ></div>
                        <GoACallout
                          type={question.calloutType}
                          heading={question.errorHeader}
                          mb="l"
                          ml="xl"
                          mr="2xl"
                          mt="l"
                          testId={`callout-${question.id}`}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: question.errorBody,
                            }}
                          />
                        </GoACallout>
                      </div>
                    </div>

                    {question.helperText && (
                      <div
                        style={{ font: 'var(--goa-typography-body-xs)' }}
                        dangerouslySetInnerHTML={{
                          __html: question.helperText,
                        }}
                      />
                    )}
                  </GoARadioGroup>
                </GoAFormItem>
                <GoASpacer vSpacing="l" />
                <GoADivider />
              </div>
            );
          })}
        </>
      )}

      <GoASpacer vSpacing="2xl" />
      {hasWarningTriggered() && (
        <GoAButton
          onClick={() => {
            dispatch(clearCollision());
            localStorage.clear();
            sessionStorage.clear();
            dispatch(logout());
            navigate('/');
          }}
        >
          {isLoggedIn ? 'Exit and logout' : 'Exit'}
        </GoAButton>
      )}
      {/* Only show the wizard button if the user has actually answered yes/no to the
          'parked vehicle hit and run?' question (wasHitAndRun !== undefined)
          AND has not triggered an ineligibility callout. */}
      {wasHitAndRun !== null &&
        wasHitAndRun !== undefined &&
        !hasWarningTriggered() &&
        hasAllQuestionsAnswered() && (
          <WizardButtonGroup
            nextPage={nextPage}
            validate={validateFlowAnswers}
            showViewSummaryButton={false}
            save={handleFlowSave}
            navigate={navigationConfiguration.navigate}
          />
        )}
    </div>
  );
};
